import React from 'react';
import type { KiwoomDtCurvePoint } from './kiwoomDayTradingPaper';

/**
 * Day trading paper 자산곡선.
 *
 * 백테스트(in-sample)와 실전 페이퍼를 **둘 다 100만 원점(x=0)에서** 겹쳐 그린다.
 * x축 = 각 계좌의 시작 이후 거래일 수. Perpetual Live equity 칸처럼 전폭·높게.
 */

const BT_COLOR = '#7c3aed88';
const LIVE_COLOR = '#c4a0ff';
const BASE_COLOR = '#7c3aed55';
const TEXT_MUTED = '#c4b5d4';
const POS = '#3fb950';
const NEG = '#f85149';

function man(v: number): string {
  if (Math.abs(v) >= 10000) return `${(v / 10000).toFixed(1)}만`;
  return Math.round(v).toLocaleString('ko-KR');
}

function seriesFrom(
  startEquity: number,
  daysEquity: number[],
  curve?: KiwoomDtCurvePoint[],
): number[] {
  if (curve && curve.length >= 1) return curve.map((p) => p.equity);
  return [startEquity, ...daysEquity];
}

export function KiwoomEquityCurveChart({
  startEquity,
  backtest,
  live,
  backtestCurve,
  liveCurve,
}: {
  startEquity: number;
  backtest: number[];
  live: number[];
  backtestCurve?: KiwoomDtCurvePoint[];
  liveCurve?: KiwoomDtCurvePoint[];
}): React.ReactElement {
  const W = 640;
  const H = 160;
  const padL = 8;
  const padR = 52;
  const padT = 12;
  const padB = 18;

  const btSeries = seriesFrom(startEquity, backtest, backtestCurve);
  const liveSeries = seriesFrom(startEquity, live, liveCurve);

  const nMax = Math.max(btSeries.length, liveSeries.length) - 1 || 1;
  const all = [...btSeries, ...liveSeries, startEquity];
  let lo = Math.min(...all);
  let hi = Math.max(...all);
  if (lo === hi) { lo -= 1; hi += 1; }
  const padY = (hi - lo) * 0.12;
  lo -= padY;
  hi += padY;

  const x = (i: number) => padL + (i / nMax) * (W - padL - padR);
  const y = (v: number) => padT + (1 - (v - lo) / (hi - lo)) * (H - padT - padB);

  const pathOf = (s: number[]) =>
    s.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');

  const yBase = y(startEquity);
  const liveLast = liveSeries[liveSeries.length - 1];
  const btLast = btSeries[btSeries.length - 1];
  const hasLive = live.length > 0 || (liveCurve != null && liveCurve.length > 1);
  const liveColor = liveLast >= startEquity ? POS : NEG;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="day trading paper equity curve"
      style={{ width: '100%', height: 120, display: 'block' }}
    >
      <line x1={padL} y1={yBase} x2={W - padR} y2={yBase} stroke={BASE_COLOR} strokeWidth={1} strokeDasharray="3 3" />
      <text x={W - padR + 4} y={yBase + 3} fill={TEXT_MUTED} fontSize={10}>{man(startEquity)}</text>

      <path d={pathOf(btSeries)} fill="none" stroke={BT_COLOR} strokeWidth={1.6} strokeDasharray="4 2.5" />
      <circle cx={x(btSeries.length - 1)} cy={y(btLast)} r={2.2} fill={BT_COLOR} />
      <text x={W - padR + 4} y={y(btLast) + 3} fill={BT_COLOR} fontSize={10}>{man(btLast)}</text>

      {hasLive && (
        <>
          <path d={pathOf(liveSeries)} fill="none" stroke={LIVE_COLOR} strokeWidth={2.2} />
          {liveSeries.map((v, i) => (
            <circle key={i} cx={x(i)} cy={y(v)} r={2} fill={LIVE_COLOR} />
          ))}
          <text x={W - padR + 4} y={y(liveLast) + 3} fill={liveColor} fontSize={10} fontWeight={700}>{man(liveLast)}</text>
        </>
      )}
      {!hasLive && (
        <circle cx={x(0)} cy={yBase} r={3.5} fill={LIVE_COLOR} />
      )}
    </svg>
  );
}

export default KiwoomEquityCurveChart;
